import React from 'react';
import PropTypes from 'prop-types';
import compose from 'recompose/compose';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { translate } from 'react-i18next';
import { Avatar, Button, Typography, Table, TableBody, TableCell, TableHead, TableRow } from '@material-ui/core';
import { DateTime } from 'luxon';
import LockOpenTwoToneIcon from '@material-ui/icons/LockOpenTwoTone';
import LockTwoToneIcon from '@material-ui/icons/LockTwoTone';
import PersonIcon from '@material-ui/icons/Person';
import PersonOutlineIcon from '@material-ui/icons/PersonOutline';
import { actions } from '../../../actions/boardActions';
import LoadingIndicator from '../../LoadingIndicator';
import NewThreadDialog from './NewThreadDialog';
import routePaths from '../../../routePaths';
import history from '../../../history';
import { generateColor } from '../../../utils/avatarColorGenerator';

class BoardThreads extends React.Component {
  state = {
    newThreadDialogVisible: false,
    submitting: false,
  };
  
  componentDidMount() {
    this.props.actions.getThreads(this.props.surveyId);
  }

  handleCreateThreadStart = () => this.setState({ newThreadDialogVisible: true });

  handleCreateThread = data => {
    this.setState({ submitting: true });
    const redirectToSurveyDetails = (!this.props.auth || !this.props.auth.signedIn) && this.props.boardSettings && this.props.boardSettings.postsPublicationMode === 'moderated';
    this.props.actions.createThread(this.props.surveyId, data, redirectToSurveyDetails)
      .finally(() => {
        this.setState({ submitting: false });
        if (redirectToSurveyDetails) this.setState({ newThreadDialogVisible: false });
      });
  }

  handleCancelThreadCreation = () => this.setState({ newThreadDialogVisible: false });

  render() {
    const { t, fetching, threads, surveyId, auth, readonly, preview, satisfyingParticipationConstraints, authenticationRequired } = this.props;

    return (
      <React.Fragment>
        <LoadingIndicator fetching={fetching}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell component="th">{t('surveyDetails.boardTab.table.author')}</TableCell>
                <TableCell component="th">{t('surveyDetails.boardTab.table.title')}</TableCell>
                <TableCell component="th" align="right" style={{ whiteSpace: 'nowrap' }}>{t('surveyDetails.boardTab.table.postsCount')}</TableCell>
                <TableCell component="th">{t('surveyDetails.boardTab.table.createdAt')}</TableCell>
                <TableCell component="th">{t('surveyDetails.boardTab.table.status')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
            {threads.map(thread => {
              let createdBy = thread.createdBy || { firstName: 'Anonimowy', lastName: 'Użytkownik' };
              if (createdBy.isDeleted) {
                createdBy = { firstName: 'Usunięty', lastName: 'Użytkownik', IsDeleted: true };
              }
              return (
                <TableRow key={thread.id} hover={!preview} onClick={preview ? null : () => history.push(routePaths.board.replace(':id', surveyId).replace(':threadId', thread.id))} style={{ cursor: preview ? 'default' : 'pointer' }}>
                  <TableCell scope="row" style={{ backgroundColor: thread.pendingApproval ? '#fff8e1' : null }}>
                    <Avatar alt={`${createdBy.firstName} ${createdBy.lastName}`} 
                      style={{ backgroundColor: thread.createdBy && !thread.createdBy.isDeleted ? generateColor(createdBy.firstName, createdBy.lastName) : null}} 
                      title={`${createdBy.firstName} ${createdBy.lastName}`}>
                        {thread.createdBy 
                        ? (thread.createdBy.isDeleted ? <PersonOutlineIcon />: `${createdBy.firstName[0]}${createdBy.lastName[0]}`) 
                        : <PersonIcon />}
                      </Avatar>
                  </TableCell>
                  <TableCell style={{ width: '100%', backgroundColor: thread.pendingApproval ? '#fff8e1' : null }}>{thread.title}</TableCell>
                  <TableCell align="right" style={{ backgroundColor: thread.pendingApproval ? '#fff8e1' : null }}>{thread.postsCount}</TableCell>
                  <TableCell style={{ whiteSpace: 'nowrap', backgroundColor: thread.pendingApproval ? '#fff8e1' : null }}>{DateTime.fromISO(thread.createdAt).toFormat('dd.MM.yyyy HH:mm')}</TableCell>
                  <TableCell style={{ backgroundColor: thread.pendingApproval ? '#fff8e1' : null }}>
                    {thread.closed || readonly ? <LockTwoToneIcon titleAccess="Zamknięty" /> : <LockOpenTwoToneIcon titleAccess="Otwarty" />}
                  </TableCell>
                </TableRow>
              );
            })}
            </TableBody>
          </Table>
          {!fetching && threads.length === 0 ? <Typography gutterBottom align="center" color="textSecondary" style={{ marginTop: 16 }}>{t('surveyDetails.boardTab.emptyState')}</Typography> : null}
        </LoadingIndicator>
        {!readonly && (!auth.signedIn || satisfyingParticipationConstraints) ? <Button variant="contained" color="primary" onClick={this.handleCreateThreadStart} style={{ marginTop: 16 }} disabled={(authenticationRequired && !auth.signedIn) || preview}>{t('surveyDetails.boardTab.newThreadButton')}</Button> : null}
        {!readonly && (authenticationRequired && !auth.signedIn) ? <Typography variant="caption" color="textSecondary" component="p" style={{ marginTop: 10 }}>Udział w dyskusji wymaga zalogowania</Typography> : null}
        {readonly && <Typography variant="caption" color="textSecondary" component="p" style={{ marginTop: 10 }}>Tworzenie nowych wątków zostało wyłączone, forum działa w trybie tylko do odczytu</Typography>}
        <NewThreadDialog
          open={this.state.newThreadDialogVisible}
          onSubmit={this.handleCreateThread}
          onCancel={this.handleCancelThreadCreation}
          submitting={this.state.submitting}
        />
      </React.Fragment>
    );
  }
}

BoardThreads.propTypes = {
  t: PropTypes.func.isRequired,
  actions: PropTypes.object.isRequired,
  fetching: PropTypes.bool.isRequired,
  threads: PropTypes.array.isRequired,
  surveyId: PropTypes.string.isRequired,
  auth: PropTypes.object.isRequired,
  readonly: PropTypes.bool,
  preview: PropTypes.bool,
  satisfyingParticipationConstraints: PropTypes.bool,
  authenticationRequired: PropTypes.bool,
  boardSettings: PropTypes.object,
};

function mapStateToProps(state) {
  return {
    fetching: state.boardThreads.fetching,
    threads: state.boardThreads.data,
    auth: state.auth,
  };
}

function mapDispatchToProps(dispatch) {
  return {
    actions: bindActionCreators(actions, dispatch)
  };
}

export default compose(
  connect(mapStateToProps, mapDispatchToProps),
  translate()
)(BoardThreads);



// WEBPACK FOOTER //
// ./src/components/consultationSurveys/board/BoardThreads.js